/**
 * Pagination des listes — conventions du guide §4.2.
 *
 * Toutes les listes utilisent `POST .../all` (jamais GET) avec un corps
 * `{ filter, page, pageSize }` ; les champs du filtre sont tous optionnels,
 * un filtre absent ou vide renvoie tout. La réponse a toujours la forme
 * `Paged<T>`. `total` est le nombre total d'éléments correspondant au
 * filtre (pas la taille de la page) — le nombre de pages pour
 * `usePagination` (core/hooks) est `Math.ceil(total / pageSize)`.
 */

export {
  MAX_PAGE_SIZE,
  MIN_PAGE,
  clampPage,
  clampPageSize,
} from "@/core/utils/func/input-normalization";

/** Corps des requêtes de liste `POST .../all`. */
export interface ListRequest<TFilter = unknown> {
  /** Filtre structuré — tous champs optionnels, `{}` ou omis = tout. */
  filter?: TFilter;
  /** Défaut serveur : 1. */
  page?: number;
  /** Défaut serveur : 20. Borne guide §4.5 : 1–200. */
  pageSize?: number;
}

/** Enveloppe de réponse des listes paginées. */
export interface Paged<T> {
  items: T[];
  /** Nombre total d'éléments correspondant au filtre. */
  total: number;
  page: number;
  pageSize: number;
}

/** Nombre de pages à partir d'une réponse paginée (pour `usePagination`). */
export const pageCount = <T>(paged: Pick<Paged<T>, "total" | "pageSize">): number =>
  paged.pageSize > 0 ? Math.ceil(paged.total / paged.pageSize) : 0;

/** Paramètres de pagination des GET de liste paginés (query `page`/`pageSize`). */
export type PageParams = Pick<ListRequest, "page" | "pageSize">;

/**
 * Paramètres des GET de liste paginés : pagination + `filters` (query params
 * additionnels — noms = champs du DTO, confirmés par le backend).
 */
export type ListQueryParams = PageParams & { filters?: Record<string, string> };

/** Défauts serveur des listes (guide §4.2) : page 1, 20 éléments par page. */
export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Normalise la réponse d'un GET de liste paginée : enveloppe `Paged<T>`
 * attendue ; tolère un tableau brut ou un objet unique (résidu transitoire
 * de rollout backend) en les enveloppant.
 */
export const normalizePaged = <T>(
  data: Paged<T> | T[] | T | null | undefined,
  pageSize: number
): Paged<T> => {
  if (data && typeof data === "object" && !Array.isArray(data) && "items" in data) {
    return data as Paged<T>;
  }
  const items = Array.isArray(data) ? data : data ? [data] : [];
  return {
    items,
    total: items.length,
    page: DEFAULT_PAGE,
    pageSize: items.length || pageSize,
  };
};

/**
 * Traduit l'état de filtres TanStack Table (`ColumnFiltersState`) en query
 * params plates pour les GET de liste paginés : `{ [columnId]: valeur }` —
 * les ids de colonnes filtrables sont les noms de champs du DTO. Valeurs
 * non textuelles ou vides ignorées (la sentinelle « Tout » est déjà épurée
 * par `DataTableClar`).
 */
export const columnFiltersToQueryParams = (
  filters: { id: string; value: unknown }[]
): Record<string, string> => {
  const query: Record<string, string> = {};
  for (const { id, value } of filters) {
    if (typeof value === "string" && value) query[id] = value;
  }
  return query;
};
